import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { DIMPLE_BUILD } from '../../data/featuredBuilds';

const BrowserFrame = ({ build, active, testIdPrefix }) => (
  <div className="relative rounded-[14px] overflow-hidden bg-[#0B0E1A] ring-1 ring-white/10 shadow-[0_40px_90px_-30px_rgba(167,70,138,0.6)]">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#151A2B] border-b border-white/5">
      <span className="w-2.5 h-2.5 rounded-full bg-[#BC516F]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#A7468A]/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
      <span className="ml-3 flex-1 truncate rounded-full bg-white/5 px-3 py-1 text-[11px] text-white/60 font-mono" data-testid={`${testIdPrefix}-url`}>
        {build.host}{build.shots[active].path === '/' ? '' : build.shots[active].path}
      </span>
    </div>
    <div className="relative aspect-[16/10]">
      {build.shots.map((s, i) => (
        <img
          key={s.key}
          src={`/portfolio/${build.folder}/${s.key}.jpg`}
          alt={s.alt}
          title={s.title}
          width="1440"
          height="900"
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${i === active ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
    </div>
  </div>
);

const ShotTabs = ({ build, active, onSelect, testIdPrefix }) => (
  <div className="mt-5 lg:mt-12 lg:pr-28 grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-3 gap-2" role="tablist">
    {build.shots.map((s, i) => {
      const Icon = s.icon;
      const on = i === active;
      return (
        <button
          key={s.key}
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onSelect(i)}
          className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors duration-300 ${on ? 'bg-gradient-to-r from-[#A7468A] to-[#BC516F] text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'}`}
          data-testid={`${testIdPrefix}-tab-${s.key}`}
        >
          <Icon className="w-3.5 h-3.5" /> {s.label}
        </button>
      );
    })}
  </div>
);

const PhoneMock = ({ build }) => (
  <div className="hidden lg:block absolute -bottom-8 -right-8 w-[130px] rounded-[26px] bg-[#0B0E1A] p-1.5 ring-1 ring-white/15 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] animate-[float_6s_ease-in-out_infinite]">
    <img
      src={`/portfolio/${build.folder}/mobile.jpg`}
      alt={build.mobileAlt}
      title={`${build.name} on mobile | Audio & Co.`}
      width="585"
      height="1266"
      loading="lazy"
      className="w-full rounded-[20px] aspect-[9/19] object-cover object-top"
    />
  </div>
);

export default function FeaturedClientBuild({ build = DIMPLE_BUILD, testIdPrefix = 'featured-build', eyebrow = 'Featured client build', caseStudyHref, headingAs: Heading = 'h2' }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % build.shots.length), 5000);
    return () => clearInterval(t);
  }, [paused, build.shots.length]);

  return (
    <section className="relative overflow-hidden bg-[#1E2A44] text-white" data-testid={testIdPrefix}>
      <div aria-hidden className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#A7468A]/25 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 right-0 w-[460px] h-[460px] rounded-full bg-[#BC516F]/20 blur-[120px]" />
      <div className="relative max-w-[1120px] mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-24 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center">
        <div>
          <p className="text-[#E7A3C4] font-semibold text-sm uppercase tracking-widest">{eyebrow}</p>
          <Heading className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3 leading-tight" data-testid={`${testIdPrefix}-title`}>
            {build.name}
          </Heading>
          <p className="text-white/60 mt-1 text-sm">{build.sub}</p>
          <p className="text-white/80 mt-5 leading-relaxed">{build.intro}</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-5 gap-y-2.5 text-[15px]" data-testid={`${testIdPrefix}-deliverables`}>
            {build.deliverables.map((d) => (
              <li key={d} className="flex gap-2.5 text-white/85">
                <span className="mt-2 w-1.5 h-1.5 shrink-0 rounded-full bg-[#BC516F]" />{d}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={build.site}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#A7468A] to-[#BC516F] px-6 py-3 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
              data-testid={`${testIdPrefix}-visit`}
            >
              Visit {build.host} <ExternalLink className="w-4 h-4" />
            </a>
            {caseStudyHref && (
              <Link
                to={caseStudyHref}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-white/10"
                data-testid={`${testIdPrefix}-case-study`}
              >
                Read the case study <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="relative">
            <a href={`${build.site}${build.shots[active].path}`} target="_blank" rel="noopener noreferrer" className="block group" data-testid={`${testIdPrefix}-screenshot`} aria-label={build.shots[active].alt}>
              <BrowserFrame build={build} active={active} testIdPrefix={testIdPrefix} />
            </a>
            <PhoneMock build={build} />
          </div>
          <ShotTabs build={build} active={active} onSelect={(i) => { setActive(i); setPaused(true); }} testIdPrefix={testIdPrefix} />
        </div>
      </div>
    </section>
  );
}
